import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2, ShoppingCart } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { SelectContainerDialog } from "./SelectContainerDialog";
import { useToast } from "@/hooks/use-toast";
import { useAddToCart } from "@/hooks/useCart";
import { ApiError } from "@/lib/api";
import { formatQuota } from "@/lib/utils";
import type { AddOnDto } from "@/types/api";

interface AddOnCardProps {
  addOn: AddOnDto;
}

/**
 * One add-on tile. Container-scoped → opens the domain picker first so the
 * boost lands on ONE specific domain; account-scoped goes straight in.
 * Both purchase through the Stripe cart — fulfilment applies the increments
 * (container allocation or account pool) the moment payment lands.
 */
export function AddOnCard({ addOn }: AddOnCardProps) {
  const isContainerScoped = addOn.scope === "Container";
  const [pickerOpen, setPickerOpen] = React.useState(false);

  const addToCart = useAddToCart();
  const navigate  = useNavigate();
  const { toast } = useToast();

  const features = featuresFor(addOn);
  const cadenceLabel = addOn.is_one_time ? "one-time" : "/mo";

  async function addAndGoToCart(containerId?: number) {
    try {
      await addToCart.mutateAsync({
        item_type:     "AddOn",
        slug:          addOn.slug,
        billing_cycle: "monthly",
        quantity:      1,
        container_id:  containerId
      });
      setPickerOpen(false);
      toast({ title: "Added to cart", description: addOn.name, variant: "success" });
      navigate("/cart");
    } catch (err) {
      const description =
        err instanceof ApiError
          ? `${err.message}${err.code ? ` (${err.code})` : ""}`
          : "Couldn't add to cart.";
      toast({ title: "Couldn't add add-on", description, variant: "destructive" });
    }
  }

  function onClickBuy() {
    if (isContainerScoped) setPickerOpen(true);
    else void addAndGoToCart();
  }

  return (
    <>
      <Card className="flex h-full flex-col p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wider text-brand-accentDark">
              {isContainerScoped ? "Per domain" : "Account-wide"}
            </p>
            <h3 className="mt-1 font-display text-lg font-bold tracking-tight sm:text-xl">{addOn.name}</h3>
          </div>
          {addOn.is_one_time && (
            <Badge variant="soft">One-time</Badge>
          )}
        </div>

        {addOn.description && (
          <p className="mt-2 text-sm text-muted-foreground">{addOn.description}</p>
        )}

        <div className="mt-4 flex items-baseline gap-1.5">
          <span className="font-display text-3xl font-bold tracking-tight">
            ${Math.round(addOn.price_monthly)}
          </span>
          <span className="text-sm text-muted-foreground">{cadenceLabel}</span>
        </div>

        {features.length > 0 && (
          <ul className="my-5 space-y-2 text-sm">
            {features.map(f => (
              <li key={f} className="flex items-start gap-2">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-green" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-auto pt-2">
          <Button
            onClick={onClickBuy}
            disabled={addToCart.isPending}
            className="w-full"
          >
            {addToCart.isPending && !isContainerScoped && <Loader2 className="h-4 w-4 animate-spin" />}
            {isContainerScoped ? "Add to a domain" : "Add to account"}
            {!addToCart.isPending && <ShoppingCart className="h-4 w-4" />}
          </Button>
        </div>
      </Card>

      {isContainerScoped && (
        <SelectContainerDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          addOnName={addOn.name}
          pending={addToCart.isPending}
          onConfirm={id => void addAndGoToCart(id)}
        />
      )}
    </>
  );
}

function featuresFor(a: AddOnDto): string[] {
  const out: string[] = [];
  if (a.mailboxes_increment > 0)   out.push(`+${formatQuota(a.mailboxes_increment)} mailboxes`);
  if (a.bulk_emails_increment > 0) out.push(`+${a.bulk_emails_increment.toLocaleString()} bulk emails / month`);
  if (a.storage_gb_increment > 0)  out.push(`+${a.storage_gb_increment} GB storage`);
  if (a.aliases_increment > 0)     out.push(`+${a.aliases_increment} aliases`);
  if (a.forwarders_increment > 0)  out.push(`+${a.forwarders_increment} forwarders`);
  if (a.websites_increment > 0)    out.push(`+${a.websites_increment} website${a.websites_increment === 1 ? "" : "s"}`);
  if (a.smtp_relays_increment > 0) out.push(`+${a.smtp_relays_increment.toLocaleString()} SMTP relays`);
  if (a.ssl_certs_increment > 0)   out.push(`+${a.ssl_certs_increment} SSL certificate${a.ssl_certs_increment === 1 ? "" : "s"}`);
  return out;
}
